const TEMPLATE_VARIABLE_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*\.[a-zA-Z][a-zA-Z0-9]*$/;
const TEMPLATE_VARIABLE_TOKEN_PATTERN = /(?<!\\)\{\{\s*([a-z][a-zA-Z0-9]*\.[a-zA-Z][a-zA-Z0-9]*)(?:\s*\|\s*default\s*:\s*"((?:\\.|[^"\\])*)")?\s*\}\}/g;
const TEMPLATE_VARIABLE_MISSING_POLICIES = Object.freeze(['fallback', 'blank', 'block']);
const TEMPLATE_VARIABLE_FORMATS = Object.freeze(['text', 'date', 'dateTime', 'currency', 'number', 'phone', 'url']);

function isTemplateVariableKey(value) {
  const key = String(value || '');
  if (['__proto__', 'prototype', 'constructor'].some((part) => key.split('.').includes(part))) return false;
  return TEMPLATE_VARIABLE_KEY_PATTERN.test(key);
}

function splitTemplateVariableKey(value) {
  const key = String(value || '').trim();
  if (!isTemplateVariableKey(key)) return null;
  const [object, property] = key.split('.');
  return { object, property, key };
}

function decodeFallback(value) {
  return typeof value === 'string' ? value.replace(/\\"/g, '"').replace(/\\\\/g, '\\') : null;
}

function parseTemplateVariableToken(value) {
  const pattern = new RegExp(`^${TEMPLATE_VARIABLE_TOKEN_PATTERN.source}$`);
  const match = pattern.exec(String(value || ''));
  if (!match) return null;
  return { ...splitTemplateVariableKey(match[1]), fallback: decodeFallback(match[2]) };
}

function normalizeTemplateVariableAttrs(attrs = {}, definition = null) {
  const parsed = splitTemplateVariableKey(attrs.key || `${attrs.object || ''}.${attrs.property || ''}`);
  if (!parsed) return null;
  const allowedMissingPolicies = definition?.allowedMissingPolicies || TEMPLATE_VARIABLE_MISSING_POLICIES;
  const missingPolicy = allowedMissingPolicies.includes(attrs.missingPolicy) ? attrs.missingPolicy : definition?.defaultMissingPolicy || 'blank';
  const formatType = attrs.format?.type;
  const allowedFormats = definition?.allowedFormats || TEMPLATE_VARIABLE_FORMATS;
  const format = allowedFormats.includes(formatType) ? { ...attrs.format } : { type: allowedFormats[0] || 'text' };
  return {
    ...parsed,
    label: attrs.label || (definition ? `${definition.objectLabel || parsed.object} · ${definition.label}` : parsed.key),
    fallback: typeof attrs.fallback === 'string' ? attrs.fallback : definition?.defaultFallback ?? null,
    missingPolicy,
    format,
    version: 1,
  };
}

function canonicalTemplateVariableText(attrs = {}) {
  const normalized = normalizeTemplateVariableAttrs(attrs);
  if (!normalized) return '';
  if (!normalized.fallback) return `{{${normalized.key}}}`;
  const fallback = normalized.fallback.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `{{${normalized.key} | default:"${fallback}"}}`;
}

function variableDefinitionMap(objects = []) {
  const definitions = new Map();
  for (const object of objects) {
    for (const property of object?.properties || []) {
      const key = `${object.key}.${property.key}`;
      if (!isTemplateVariableKey(key)) continue;
      definitions.set(key, { ...property, key, object: object.key, property: property.key, objectLabel: object.label });
      for (const alias of property.aliases || []) {
        if (isTemplateVariableKey(alias)) definitions.set(alias, { ...property, key, object: object.key, property: property.key, objectLabel: object.label, alias });
      }
    }
  }
  return definitions;
}

function normalizeTemplateContext(context) {
  const key = String(context?.key || '').trim();
  if (!key) return null;
  const allowedObjects = [...new Set((context.allowedObjects || []).map((value) => String(value || '').trim()).filter(Boolean))];
  const allowedVariables = context.allowedVariables == null ? null : [...new Set(context.allowedVariables.map((value) => String(value || '').trim()).filter(isTemplateVariableKey))];
  const requiredVariables = [...new Set((context.requiredVariables || []).map((value) => String(value || '').trim()).filter(isTemplateVariableKey))];
  return { key, label: String(context.label || key), allowedObjects, allowedVariables, requiredVariables };
}

function filterTemplateObjectsForContext(objects = [], context) {
  const normalized = normalizeTemplateContext(context);
  if (!normalized) return [];
  const allowedObjects = new Set(normalized.allowedObjects);
  const allowedVariables = normalized.allowedVariables ? new Set(normalized.allowedVariables) : null;
  return objects
    .filter((object) => allowedObjects.has(object.key))
    .map((object) => ({ ...object, properties: (object.properties || []).filter((property) => !allowedVariables || allowedVariables.has(`${object.key}.${property.key}`)) }))
    .filter((object) => object.properties.length > 0);
}

function contextFromObjectContract(key, label, contract = {}) {
  const objects = Array.isArray(contract.objects) ? contract.objects : [];
  return normalizeTemplateContext({
    key,
    label,
    allowedObjects: objects.map((object) => object.key),
    allowedVariables: objects.flatMap((object) => (object.properties || []).map((property) => `${object.key}.${property.key}`)),
    requiredVariables: objects.flatMap((object) => (object.properties || []).filter((property) => property.required === true).map((property) => `${object.key}.${property.key}`)),
  });
}

function textPartsToTemplateVariables(node, definitions) {
  const text = String(node.text || '');
  const content = [];
  let cursor = 0;
  TEMPLATE_VARIABLE_TOKEN_PATTERN.lastIndex = 0;
  let match = TEMPLATE_VARIABLE_TOKEN_PATTERN.exec(text);
  while (match) {
    const definition = definitions.get(match[1]);
    if (!definition) {
      match = TEMPLATE_VARIABLE_TOKEN_PATTERN.exec(text);
      continue;
    }
    if (match.index > cursor) content.push({ ...node, text: text.slice(cursor, match.index) });
    const attrs = normalizeTemplateVariableAttrs({ key: definition.key, fallback: decodeFallback(match[2]), missingPolicy: definition.defaultMissingPolicy, format: { type: definition.allowedFormats?.[0] || definition.type || 'text' } }, definition);
    content.push({ type: 'templateVariable', attrs, ...(node.marks ? { marks: node.marks } : {}) });
    cursor = match.index + match[0].length;
    match = TEMPLATE_VARIABLE_TOKEN_PATTERN.exec(text);
  }
  TEMPLATE_VARIABLE_TOKEN_PATTERN.lastIndex = 0;
  if (!content.length) return [node];
  if (cursor < text.length) content.push({ ...node, text: text.slice(cursor) });
  return content;
}

function convertLegacyTemplateVariables(document, objects = []) {
  const definitions = variableDefinitionMap(objects);
  if (!document || typeof document !== 'object' || definitions.size === 0) return document;
  const visit = (node, excluded = false) => {
    if (!node || typeof node !== 'object') return node;
    const isExcluded = excluded || ['codeBlock', 'code'].includes(node.type);
    if (node.type === 'text' && !isExcluded) return textPartsToTemplateVariables(node, definitions);
    if (!Array.isArray(node.content)) return { ...node };
    return { ...node, content: node.content.flatMap((child) => {
      const converted = visit(child, isExcluded);
      return Array.isArray(converted) ? converted : [converted];
    }) };
  };
  return visit(document);
}

export {
  TEMPLATE_VARIABLE_FORMATS,
  TEMPLATE_VARIABLE_KEY_PATTERN,
  TEMPLATE_VARIABLE_MISSING_POLICIES,
  TEMPLATE_VARIABLE_TOKEN_PATTERN,
  canonicalTemplateVariableText,
  contextFromObjectContract,
  convertLegacyTemplateVariables,
  filterTemplateObjectsForContext,
  isTemplateVariableKey,
  normalizeTemplateVariableAttrs,
  normalizeTemplateContext,
  parseTemplateVariableToken,
  splitTemplateVariableKey,
  variableDefinitionMap,
};
